import React, { useEffect, useRef } from 'react';

// Lightweight rich-text editor — no external deps. Uses contentEditable plus a
// small toolbar of execCommand actions (still the most reliable cross-browser
// way to do this without pulling in TipTap/Quill). Output is HTML; the server
// sanitizes against an allowlist before storage so anything pasted in gets
// stripped to safe markup. Plain-text paste is enforced on `paste` to keep
// styles from sneaking through.
//
//   <RichTextEditor value={html} onChange={setHtml} placeholder="Type a reply…" />

const TOOLS = [
  { cmd: 'bold',          label: 'B',  style: { fontWeight: 700 } },
  { cmd: 'italic',        label: 'I',  style: { fontStyle: 'italic' } },
  { cmd: 'underline',     label: 'U',  style: { textDecoration: 'underline' } },
  { cmd: 'insertUnorderedList', label: '• List' },
  { cmd: 'insertOrderedList',   label: '1. List' },
  { cmd: 'createLink',    label: '🔗 Link' },
  { cmd: 'removeFormat',  label: 'Clear' },
];

export default function RichTextEditor({ value, onChange, placeholder, minHeight = 100, disabled }) {
  const ref = useRef(null);
  const lastValueRef = useRef('');

  // Sync external value into the contenteditable WITHOUT clobbering the caret
  // when the user is actively typing. We only apply external changes when the
  // value differs from what we last emitted.
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastValueRef.current) {
      ref.current.innerHTML = value || '';
      lastValueRef.current = value || '';
    }
  }, [value]);

  const exec = (cmd) => {
    if (disabled) return;
    if (cmd === 'createLink') {
      const url = window.prompt('Link URL:', 'https://');
      if (!url) return;
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand(cmd, false, null);
    }
    handleInput();
  };

  const handleInput = () => {
    const html = ref.current?.innerHTML || '';
    lastValueRef.current = html;
    onChange?.(html);
  };

  // Force plain-text paste (strips styles, scripts, fonts).
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const showPlaceholder = !value;

  return (
    <div style={{ border: '1px solid var(--border-color, #cbd5e1)', borderRadius: 6, background: '#fff' }}>
      <div role="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 6, borderBottom: '1px solid var(--border-color, #e2e8f0)', background: '#f8fafc' }}>
        {TOOLS.map(t => (
          <button
            key={t.cmd}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(t.cmd)}
            disabled={disabled}
            style={{ padding: '4px 10px', fontSize: 13, border: '1px solid #e2e8f0', background: '#fff', borderRadius: 4, cursor: 'pointer', ...t.style }}
            title={t.cmd}
          >{t.label}</button>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          style={{
            minHeight, padding: '10px 12px', fontSize: 14, lineHeight: 1.5,
            outline: 'none', color: 'inherit',
          }}
        />
        {showPlaceholder && (
          <div aria-hidden="true" style={{
            position: 'absolute', top: 10, left: 12, color: '#94a3b8',
            pointerEvents: 'none', fontSize: 14,
          }}>{placeholder || 'Type a message…'}</div>
        )}
      </div>
    </div>
  );
}
